import { useEffect, useMemo, useState, type Dispatch, type SetStateAction } from 'react'
import type {
  AlbumConfig,
  AlbumRecord,
  AppState,
  InvestmentDestination
} from '../../../core/domain'
import {
  ALBUM_PAGE_SIZE_OPTIONS,
  addDestinationRecord,
  backToSelectorState,
  createAlbumState,
  deleteAlbumState,
  getAvailableAlbumPageSizes,
  pruneStickersToSizeRecord,
  removeDestinationRecord,
  removeStickerInvestmentRecord,
  resolveAlbumPageSize,
  saveStickerInvestmentRecord,
  selectAlbumState,
  updateActiveRecordState,
  updateAlbumRecord
} from './state'

export type StickerView = 'album' | 'flipbook'
export type MiddleFrameTab = 'album' | 'history' | 'configuration'

interface UseAlbumWorkspaceOptions {
  appState: AppState
  setAppState: Dispatch<SetStateAction<AppState>>
  albumPageSize: number
  setAlbumPageSize: Dispatch<SetStateAction<number>>
}

export function useAlbumWorkspace(options: UseAlbumWorkspaceOptions) {
  const [stickerView, setStickerView] = useState<StickerView>('album')
  const [middleFrameTab, setMiddleFrameTab] = useState<MiddleFrameTab>('album')
  const [selectedSticker, setSelectedSticker] = useState<number | null>(null)
  const [selectedDestinationId, setSelectedDestinationId] = useState('')
  const [comments, setComments] = useState('')
  const [isTargetVisible, setIsTargetVisible] = useState(false)
  const [justCollectedSticker, setJustCollectedSticker] = useState<number | null>(null)

  const activeRecord = useMemo(
    () =>
      options.appState.albums.find((a) => a.config.id === options.appState.activeAlbumId) ?? null,
    [options.appState]
  )

  const availableAlbumPageSizeOptions = useMemo(
    () =>
      activeRecord ? getAvailableAlbumPageSizes(activeRecord.config.size) : ALBUM_PAGE_SIZE_OPTIONS,
    [activeRecord]
  )

  useEffect(() => {
    if (!activeRecord) return
    const resolvedPageSize = resolveAlbumPageSize(options.albumPageSize, activeRecord.config.size)
    if (resolvedPageSize !== options.albumPageSize) {
      options.setAlbumPageSize(resolvedPageSize)
    }
  }, [activeRecord, options])

  function updateActiveRecord(updater: (prev: AlbumRecord) => AlbumRecord): void {
    options.setAppState((state) => updateActiveRecordState(state, updater))
  }

  useEffect(() => {
    if (!activeRecord) return
    const size = activeRecord.config.size
    const excess = Object.keys(activeRecord.stickers).filter((k) => Number(k) > size)
    if (excess.length === 0) return
    updateActiveRecord((prev) => pruneStickersToSizeRecord(prev, size))
    // Only re-run when the album size changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeRecord?.config.size])

  useEffect(() => {
    if (selectedSticker === null || !activeRecord) return
    const existing = activeRecord.stickers[selectedSticker]
    setSelectedDestinationId(
      existing?.destinationId ?? activeRecord.config.savedInvestments[0]?.id ?? ''
    )
    setComments(existing?.comments ?? '')
    // Only re-sync modal fields when the selected sticker changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSticker])

  const stickerNumbers = useMemo(
    () => (activeRecord ? Array.from({ length: activeRecord.config.size }, (_, i) => i + 1) : []),
    // Sequence depends only on the album size.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeRecord?.config.size]
  )

  function handleSelectAlbum(albumId: string): void {
    options.setAppState((state) => selectAlbumState(state, albumId))
    setStickerView('album')
    setMiddleFrameTab('album')
    setSelectedSticker(null)
  }

  function handleCreateAlbum(record: AlbumRecord): void {
    options.setAppState((state) => createAlbumState(state, record))
    setStickerView('album')
    setMiddleFrameTab('album')
  }

  function handleDeleteAlbum(albumId: string): void {
    options.setAppState((state) => deleteAlbumState(state, albumId))
  }

  function handleBackToSelector(): void {
    options.setAppState((state) => backToSelectorState(state))
    setSelectedSticker(null)
  }

  function updateAlbum<K extends keyof AlbumConfig>(key: K, value: AlbumConfig[K]): void {
    updateActiveRecord((prev) => updateAlbumRecord(prev, key, value))
  }

  function addDestination(dest: InvestmentDestination): void {
    updateActiveRecord((prev) => addDestinationRecord(prev, dest))
  }

  function removeDestination(destId: string): void {
    updateActiveRecord((prev) => removeDestinationRecord(prev, destId))
  }

  function closeModal(): void {
    setSelectedSticker(null)
    setComments('')
  }

  function saveStickerInvestment(): void {
    if (selectedSticker === null || !selectedDestinationId || !activeRecord) return
    const wasCollected = !!activeRecord.stickers[selectedSticker]
    updateActiveRecord((prev) =>
      saveStickerInvestmentRecord(prev, selectedSticker, selectedDestinationId, comments)
    )
    closeModal()
    if (!wasCollected) setJustCollectedSticker(selectedSticker)
  }

  function removeStickerInvestment(stickerNumber: number): void {
    updateActiveRecord((prev) => removeStickerInvestmentRecord(prev, stickerNumber))
    closeModal()
  }

  return {
    activeRecord,
    availableAlbumPageSizeOptions,
    stickerNumbers,
    stickerView,
    setStickerView,
    middleFrameTab,
    setMiddleFrameTab,
    selectedSticker,
    setSelectedSticker,
    selectedDestinationId,
    setSelectedDestinationId,
    comments,
    setComments,
    isTargetVisible,
    setIsTargetVisible,
    justCollectedSticker,
    setJustCollectedSticker,
    handleSelectAlbum,
    handleCreateAlbum,
    handleDeleteAlbum,
    handleBackToSelector,
    updateAlbum,
    addDestination,
    removeDestination,
    closeModal,
    saveStickerInvestment,
    removeStickerInvestment
  }
}
